import { useState } from 'react';

const steps = [
  {
    number: '01',
    title: "We Find What You're Losing.",
    body: "Before we change anything, we audit your current setup. Most businesses are overpaying by 15–30% and have no idea. We show you exactly where it's going — line by line.",
    icon: 'ri-search-eye-line',
  },
  {
    number: '02',
    title: 'We Build Around Your Business.',
    body: "No templates. No one-size-fits-all pricing. We design infrastructure around how your business actually operates — your volume, your channels, your cash flow needs.",
    icon: 'ri-draft-line',
  },
  {
    number: '03',
    title: "We Move You Over. You Don't Feel It.",
    body: "Switching processors sounds painful. We've done this enough times to make it not be. Zero downtime, no disruption to cash flow, and we handle every detail of the transition.",
    icon: 'ri-settings-3-line',
  },
  {
    number: '04',
    title: 'We Stay. And Keep Making It Better.',
    body: "Most vendors disappear after go-live. We don't. We stay embedded — monitoring, optimizing, and improving your setup as your business grows and your needs change.",
    icon: 'ri-line-chart-line',
  },
];

export default function OurApproach() {
  const [active, setActive] = useState<number | null>(null);

  return (
    <section id="approach" className="bg-[#F9F9F7] pt-28">
      <div className="max-w-[1400px] mx-auto px-5 md:px-[75px]">

        {/* Two-col header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end mb-20">
          <div>
            <p
              className="text-[#a8894a] text-[10px] uppercase tracking-[0.25em] font-medium mb-5"
              style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
            >
              Our Process
            </p>
            <h2
              className="text-[#061e45] text-4xl md:text-5xl lg:text-[54px] font-light leading-[1.1]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              How It Works
            </h2>
          </div>
          <blockquote className="border-l-2 border-[#d3b366] pl-6 md:pl-8">
            <p
              className="text-[#061e45]/70 text-[15px] md:text-[17px] lg:text-[19px] leading-relaxed"
              style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
            >
              From first audit to full optimization, we stay embedded at every stage. No handoffs. No disappearing acts. Just a partner who sees it through.
            </p>
          </blockquote>
        </div>

        {/* Steps — 2 column grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1A1A1A]/10">
          {steps.map((step, i) => (
            <div
              key={step.number}
              onClick={() => setActive(active === i ? null : i)}
              className={`group p-10 cursor-pointer transition-all duration-300 ${
                active === i ? 'bg-white' : 'bg-[#F9F9F7] hover:bg-white'
              }`}
            >
              {/* Number + icon row */}
              <div className="flex items-center justify-between mb-8">
                <span
                  className={`text-5xl font-light leading-none transition-colors duration-300 ${
                    active === i ? 'text-[#a8894a]' : 'text-[#1A1A1A]/15 group-hover:text-[#a8894a]/50'
                  }`}
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {step.number}
                </span>
                <div className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${
                  active === i ? 'bg-[#061e45]' : 'bg-[#1A1A1A]/6 group-hover:bg-[#061e45]/10'
                }`}>
                  <i className={`${step.icon} text-lg transition-colors duration-300 ${
                    active === i ? 'text-[#d3b366]' : 'text-[#1A1A1A]/30 group-hover:text-[#061e45]'
                  }`} />
                </div>
              </div>

              {/* Title */}
              <h3
                className={`text-xl font-medium leading-snug mb-4 transition-colors duration-300 ${
                  active === i ? 'text-[#061e45]' : 'text-[#1A1A1A]/70 group-hover:text-[#061e45]'
                }`}
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                {step.title}
              </h3>

              {/* Body — always visible */}
              <p
                className="text-[#1A1A1A]/50 text-[15px] leading-relaxed"
                style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
              >
                {step.body}
              </p>
            </div>
          ))}
        </div>

      </div>

      {/* Floating Glass Card CTA */}
      <div className="relative mt-16 overflow-hidden" style={{ background: '#061e45' }}>
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 100% at 50% 50%, #1a5a9a 0%, #0d3a6e 35%, #061e45 70%, #030f24 100%)' }} />

        {/* Frosted glass card floating in center */}
        <div className="relative z-10 px-5 md:px-[75px] py-20 flex items-center justify-center">
          <div
            className="w-full max-w-[1100px] grid grid-cols-1 lg:grid-cols-2 gap-0"
            style={{
              background: 'rgba(255,255,255,0.08)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <div className="p-6 md:p-12 lg:border-r border-white/10">
              <p
                className="text-[#d3b366] text-[10px] uppercase tracking-[0.25em] font-medium mb-4"
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                Ready to Start?
              </p>
              <h3
                className="text-white text-2xl md:text-3xl font-light leading-snug"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                See exactly where your money goes and what it takes to get it back.
              </h3>
            </div>
            <div className="p-6 md:p-12 flex flex-col justify-center gap-4">
              <a
                href="/contact"
                className="whitespace-nowrap inline-flex items-center justify-center gap-3 text-[12px] uppercase tracking-[0.18em] font-semibold px-8 py-4 bg-[#d3b366] text-[#122852] hover:bg-white hover:text-[#122852] transition-all duration-200 cursor-pointer"
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                Start Your Discovery
                <i className="ri-arrow-right-line" />
              </a>
              <a
                href="/solutions"
                className="whitespace-nowrap inline-flex items-center justify-center gap-3 text-[12px] uppercase tracking-[0.18em] font-medium px-8 py-4 border border-white/25 text-white/70 hover:border-white/50 hover:text-white transition-all duration-200 cursor-pointer"
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                Explore Capabilities
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
